import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  Grid3x3,
  List,
  Table,
  Bell,
  ChevronRight,
  EllipsisVertical,
  LogOut,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth, logout } from "wasp/client/auth";
import { Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { globalSearch } from "wasp/client/operations";
import { userMenuItems } from "../../user/constants";
import { UserDropdown } from "../../user/UserDropdown";
import DarkModeSwitcher from "../../client/components/DarkModeSwitcher";
import ViewModeToggle from "../../client/components/ViewModeToggle";
import LocationDropdown from "../../client/components/Sidebar/LocationDropdown";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useNotification } from "../../client/contexts/NotificationContext";
import SearchResultsModal from "../search/SearchResultsModal";
import { LayoutConfig } from "./types";
import { ReportingTrigger } from "../../gedoba/reporting/components/ReportingTrigger";
import LocationSelectorMobile from "./LocationSelectorMobile";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";

interface CommonTopBarProps {
  isSidebarOpen: boolean;
  onSidebarToggle: () => void;
  config: LayoutConfig;
  activeView?: "cards" | "list" | "table";
  onViewChange?: (view: "cards" | "list" | "table") => void;
}

export default function CommonTopBar({
  isSidebarOpen,
  onSidebarToggle,
  config,
  activeView = "cards",
  onViewChange,
}: CommonTopBarProps) {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const { unreadCount, setIsSidebarOpen } = useNotification();
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isLocationSheetOpen, setIsLocationSheetOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Open search modal when search term is entered
  useEffect(() => {
    if (debouncedSearchTerm.trim().length >= 2) {
      setIsSearchModalOpen(true);
    } else {
      setIsSearchModalOpen(false);
    }
  }, [debouncedSearchTerm]);

  // Search query
  const { data: searchResults, isLoading: isSearchLoading } = useQuery(
    globalSearch,
    {
      locationId: selectedLocation?.id || "",
      searchTerm: debouncedSearchTerm.trim(),
      limit: 50,
    },
    {
      enabled: !!selectedLocation?.id && debouncedSearchTerm.trim().length >= 2,
    },
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsRef.current &&
        !actionsRef.current.contains(event.target as Node)
      ) {
        setIsActionsOpen(false);
      }
      if (
        isSearchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsSearchExpanded(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchExpanded]);

  useEffect(() => {
    if (isSearchExpanded) {
      searchInputRef.current?.focus();
    }
  }, [isSearchExpanded]);

  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const responsiveActions = (
    <div className="xl:hidden">
      {config.showSearchBar && (
        <DropdownMenuItem 
          onClick={() => {
            setIsSearchModalOpen(true);
            setSearchTerm("");
          }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
        >
          <Search className="size-4 text-muted-foreground" />
          <span className="font-medium">Search Everything</span>
        </DropdownMenuItem>
      )}

      {config.showViewSwitcher && onViewChange && (
        <>
          <DropdownMenuSeparator className="my-1.5" />
          <div className="px-3 py-2">
            <p className="text-[10px] uppercase font-bold text-muted-foreground/60 tracking-widest mb-2 px-1">
              Display View
            </p>
            <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg">
              <button
                onClick={() => onViewChange("cards")}
                className={cn(
                  "p-2 rounded-md flex-1 flex justify-center transition-all",
                  activeView === "cards" 
                    ? "bg-popover shadow-sm text-primary" 
                    : "text-muted-foreground hover:bg-muted/60"
                )}
              >
                <Grid3x3 className="size-4" />
              </button>
              <button
                onClick={() => onViewChange("list")}
                className={cn(
                  "p-2 rounded-md flex-1 flex justify-center transition-all",
                  activeView === "list" 
                    ? "bg-popover shadow-sm text-primary" 
                    : "text-muted-foreground hover:bg-muted/60"
                )}
              >
                <List className="size-4" />
              </button>
              <button
                onClick={() => onViewChange("table")}
                className={cn(
                  "p-2 rounded-md flex-1 flex justify-center transition-all",
                  activeView === "table" 
                    ? "bg-popover shadow-sm text-primary" 
                    : "text-muted-foreground hover:bg-muted/60"
                )}
              >
                <Table className="size-4" />
              </button>
            </div>
          </div>
        </>
      )}

      <DropdownMenuSeparator className="my-1.5" />
      
      <div className="px-3 py-2 flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Theme Mode</span>
        <DarkModeSwitcher />
      </div>

      <div className="px-1 py-1">
        <ReportingTrigger />
      </div>

      {config.showNotifications && (
        <DropdownMenuItem 
          onClick={() => {
            setIsSidebarOpen(true);
          }}
          className="px-3 py-2.5 rounded-lg cursor-pointer flex items-center gap-3"
        >
          <Bell className="size-4 text-muted-foreground" />
          <span className="font-medium">Notifications</span>
          {unreadCount > 0 && (
            <span className="ml-auto bg-primary text-primary-foreground h-5 min-w-[20px] px-1 rounded-full text-[10px] flex items-center justify-center font-bold">
              {unreadCount}
            </span>
          )}
        </DropdownMenuItem>
      )}
    </div>
  );

  return (
    <header
      className={cn(
        "fixed top-0 right-0 z-30 bg-header-bg border-b border-header-border transition-all duration-300",
        isSidebarOpen ? "lg:left-[var(--sidebar-width)] left-0" : "left-0",
      )}
      style={{
        height: "var(--header-height)",
        padding: "0.75rem 1rem",
        minHeight: "3.5rem",
        maxHeight: "5rem",
      }}
    >
      <div className="flex items-center justify-between w-full h-full gap-4 px-2 sm:px-4">
        {/* Left Section: sidebar button, location, breadcrumbs */}
        <div className={cn(
          "flex items-center gap-3",
          isCompact ? "flex-none w-10" : "flex-none"
        )}>
          {/* Expand Sidebar Button (only when closed) */}
          {!isSidebarOpen && !config.forceSidebarCollapse && (
            <button
              onClick={onSidebarToggle}
              className="p-1.5 hover:bg-sidebar-hover rounded-lg transition-colors flex-shrink-0"
              title="Expand sidebar"
            >
              <ChevronRight className="w-5 h-5 text-header-text-muted" />
            </button>
          )}


          {/* Location Selector (Desktop only) */}
          {config.showLocationDropdown && !isCompact && (
            <div className="flex-shrink-0 max-w-sm hidden sm:block">
              <LocationDropdown />
            </div>
          )}

          {/* Breadcrumbs / custom content (desktop only) */}
          {!isCompact && config.customTopBarContent && (
            <div className="hidden lg:flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              {config.customTopBarContent}
            </div>
          )}
        </div>

        {/* Center Section: View Mode Toggle (Desktop) / Location Name (Mobile) */}
        <div className="flex-1 flex items-center justify-center px-4 min-w-0">
          {isCompact ? (
            <button 
              onClick={() => setIsLocationSheetOpen(true)}
              className="flex flex-col items-center min-w-0 max-w-full hover:bg-sidebar-hover p-1 rounded-lg transition-colors group px-4"
            >
               <div className="flex items-center gap-1.5 min-w-0">
                 <span className="text-sm font-bold text-header-text truncate text-center group-hover:text-primary transition-colors">
                  {(selectedLocation as any)?.name || "Select Location"}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-header-text-muted rotate-90 group-hover:text-primary transition-colors flex-shrink-0" />
               </div>
              {config.brandSubtitle && (
                 <span className="text-[10px] text-header-text-muted truncate">
                   {config.brandSubtitle}
                 </span>
              )}
            </button>
          ) : (
            config.showViewModeToggle && <ViewModeToggle />
          )}
        </div>

        {/* Right Section */}
        <div className={cn(
          "flex items-center justify-end",
          isCompact ? "flex-none w-10" : "flex-none"
        )}>
          {isCompact ? (
            <div className="relative" ref={actionsRef}>

            <button
              onClick={() => setIsActionsOpen((prev) => !prev)}
              className="p-2 rounded-lg hover:bg-sidebar-hover transition-colors"
              title="Open quick actions"
            >
              <EllipsisVertical className="w-5 h-5 text-header-text-muted" />
            </button>

            {isActionsOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-content-bg border border-border rounded-lg shadow-xl z-40 p-3 space-y-3 animate-in fade-in zoom-in duration-200">
                {config.showSearchBar && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-input-placeholder" />
                    <input
                      type="text"
                      placeholder={config.searchPlaceholder || "Explore..."}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => {
                        if (searchTerm.trim().length >= 2) {
                          setIsSearchModalOpen(true);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}

                {/* Main sections for compact mode */}
                <div className="flex flex-col gap-2">
                   <div className="flex items-center justify-between p-2 rounded-lg bg-sidebar-hover/50">
                    <span className="text-sm font-medium text-header-text-muted">Appearance</span>
                    <DarkModeSwitcher />
                  </div>
                  
                  <div className="flex items-center justify-between p-2 rounded-lg bg-sidebar-hover/50">
                    <span className="text-sm font-medium text-header-text-muted">Report Issue</span>
                    <ReportingTrigger />
                  </div>

                  <button
                    className="flex items-center justify-between p-2 rounded-lg bg-sidebar-hover/50 w-full"
                    onClick={() => {
                      setIsActionsOpen(false);
                      setIsSidebarOpen(true);
                    }}
                  >
                    <span className="text-sm font-medium text-header-text-muted">Notifications</span>
                    <div className="relative">
                      <Bell className="w-5 h-5 text-header-text-muted" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center rounded-full">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                  </button>
                </div>

                <div className="h-px bg-border my-2" />

                {user ? (
                  <div className="flex flex-col gap-1">
                    {userMenuItems.map((item) => {
                      if (item.isAdminOnly && !user?.isAdmin) return null;
                      return (
                        <Link
                          key={item.name}
                          to={item.to}
                          onClick={() => setIsActionsOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-hover transition-colors text-content-text"
                        >
                          <item.icon className="w-4 h-4 text-header-text-muted" />
                          <span className="text-sm">{item.name}</span>
                        </Link>
                      );
                    })}
                    <button
                      onClick={() => {
                        setIsActionsOpen(false);
                        logout();
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-hover transition-colors text-destructive"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm font-medium">Log Out</span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center justify-center w-full px-4 py-2.5 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-sm"
                    onClick={() => setIsActionsOpen(false)}
                  >
                    Login to Ottawa University
                  </Link>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-end gap-4 sm:gap-6 flex-1">
            {/* These icons are hidden on screens smaller than xl and moved to UserDropdown */}
            <div className="hidden xl:flex items-center gap-4 sm:gap-6">
              {/* Expandable search */}
              {config.showSearchBar && (
                <div
                  ref={searchContainerRef}
                  className="flex items-center justify-end"
                >
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={config.searchPlaceholder || "Explore..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {
                      if (searchTerm.trim().length >= 2) {
                        setIsSearchModalOpen(true);
                      }
                    }}
                    className={cn(
                      "bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ease-out",
                      isSearchExpanded
                        ? "w-56 pl-3 pr-3 py-1.5 opacity-100"
                        : "w-0 opacity-0 overflow-hidden border-0 p-0",
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => setIsSearchExpanded((prev) => !prev)}
                    className="p-2 hover:bg-sidebar-hover rounded-lg transition-colors"
                    title="Search"
                  >
                    <Search className="w-5 h-5 text-header-text-muted transition-colors hover:text-primary" />
                  </button>
                </div>
              )}

              {config.showViewSwitcher && onViewChange && (
                <div className="flex items-center gap-1 bg-button-bg p-1 rounded-lg border border-border/50">
                  <button
                    onClick={() => onViewChange("cards")}
                    className={cn(
                      "p-2 rounded transition-all",
                      activeView === "cards"
                        ? "bg-content-bg shadow-sm text-primary"
                        : "text-content-text-muted hover:bg-button-bg-hover",
                    )}
                    title="Cards view"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onViewChange("list")}
                    className={cn(
                      "p-2 rounded transition-all",
                      activeView === "list"
                        ? "bg-content-bg shadow-sm text-primary"
                        : "text-content-text-muted hover:bg-button-bg-hover",
                    )}
                    title="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onViewChange("table")}
                    className={cn(
                      "p-2 rounded transition-all",
                      activeView === "table"
                        ? "bg-content-bg shadow-sm text-primary"
                        : "text-content-text-muted hover:bg-button-bg-hover",
                    )}
                    title="Table view"
                  >
                    <Table className="w-4 h-4" />
                  </button>
                </div>
              )}

              <DarkModeSwitcher />

              <ReportingTrigger />

              {config.showNotifications && (
                <button
                  className="relative p-2 hover:bg-sidebar-hover rounded-lg transition-colors group"
                  title="Notifications"
                  onClick={() => setIsSidebarOpen(true)}
                >
                  <Bell className="w-5 h-5 text-header-text-muted group-hover:text-primary transition-colors" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center rounded-full scale-90">
                      {unreadCount > 9 ? '9' : unreadCount}
                    </span>
                  )}
                </button>
              )}
            </div>

            {user ? (
              <UserDropdown user={user} actions={responsiveActions} />
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 text-base font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
              >
                Login
              </Link>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Search Results Modal */}
      <SearchResultsModal
        isOpen={isSearchModalOpen}
        onClose={() => {
          setIsSearchModalOpen(false);
          setSearchTerm("");
        }}
        searchTerm={debouncedSearchTerm}
        results={searchResults?.results || []}
        isLoading={isSearchLoading}
        total={searchResults?.total || 0}
        byType={
          searchResults?.byType || {
            notices: 0,
            events: 0,
            tenants: 0,
            facilities: 0,
            buySell: 0,
            foodCourt: 0,
            lostFound: 0,
            retail: 0,
            services: 0,
          }
        }
      />

      <LocationSelectorMobile 
        isOpen={isLocationSheetOpen}
        onClose={() => setIsLocationSheetOpen(false)}
      />
    </header>
  );
}
